import { DEBUG } from '@glimmer/env';
import { DEBUG_IDENTIFIER_BUCKET } from './identifer-debug-consts';

/*
  DEBUG only fields. Keeping this in a separate interface
  which Typescript then merges with the class definition allow us to
  have a DEBUG only property without paying the cost of that field being
  present and initialized (usually to void 0) in production.
*/

/**
 * @class WeakCache
 * @extends WeakMap
 * @internal
 */
class WeakCache extends WeakMap {
  constructor(_fieldName) {
    super();
    if (DEBUG) {
      this._fieldName = _fieldName;
      this._symbol = Symbol.for(_fieldName);
    }
  }

  /**
   * Retrieve the value for a key from the WeakCache with the expectation
   * that the key exists. Throws the error generated by _expectMsg in DEBUG if
   * that key does not exist.
   *
   * @method getWithError
   * @param obj
   * @internal
   */
  getWithError(obj) {
    let v = this.get(obj);
    if (DEBUG && v === undefined) {
      throw new Error(this._expectMsg(obj));
    }
    return v;
  }

  /**
   * Retrieve the value for a key from the WeakCache, or generate one
   * using the configured _generator method if no value is yet present.
   *
   * @method lookup
   * @param obj
   * @internal
   */
  lookup(obj) {
    let v = super.get(obj);
    if (v === undefined) {
      v = this._generator(obj);
      super.set(obj, v);
      if (DEBUG) {
        if (obj[DEBUG_IDENTIFIER_BUCKET] && this._fieldName !== 'identifier-proxy-target') {
          const target = obj[Symbol.for('identifier-proxy-target')];
          target[this._symbol] = v;
        } else {
          obj[this._symbol] = v;
        }
      }
    }
    return v;
  }
}
class DebugWeakCache extends WeakCache {
  set(obj, value) {
    if (DEBUG && super.has(obj) && this.get(obj) !== value) {
      throw new Error(`${Object.prototype.toString.call(obj)} was already assigned a value for ${this._fieldName}`);
    }
    if (DEBUG) {
      if (obj[DEBUG_IDENTIFIER_BUCKET] && this._fieldName !== 'identifier-proxy-target') {
        const target = obj[Symbol.for('identifier-proxy-target')];
        target[this._symbol] = value;
        // TODO the Proxy check here is entirely for ember-m3
        // as it's attribute access tests fail since the symbol
        // is an unexpected key received by it's proxies.
        // we should address this upstream.
      } else if (obj.constructor?.toString?.() !== 'MegamorphicModel') {
        try {
          obj[this._symbol] = value;
        } catch {
          // some keys are proxies that can't accept symbol values
          // for instance records from ember-m3
        }
      }
    }
    return super.set(obj, value);
  }
}
export default DEBUG ? DebugWeakCache : WeakCache;